import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Check, Image, Search } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

const TeamNameAutocomplete = ({ value, onChange, placeholder, className }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const searchTeams = async (query) => {
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.searchTeamNames(query);
      setSuggestions(res.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    searchTeams(newValue);
  };

  const handleSelect = (team) => {
    onChange(team.name);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      setShowSuggestions(false);
      // Save new team name if it doesn't exist
      if (value && value.trim()) {
        api.addTeamName(value.trim()).catch(() => {});
      }
    }, 200);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onFocus={() => value && searchTeams(value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {loading && (
        <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 animate-pulse" />
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((team) => (
            <div
              key={team.id}
              className="px-4 py-2 hover:bg-gray-700 cursor-pointer flex items-center justify-between"
              onClick={() => handleSelect(team)}
            >
              <span className="text-white">{team.name}</span>
              <span className="text-gray-500 text-xs">used {team.usage_count}x</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Media Picker Dialog Component
const MediaPickerDialog = ({ open, onOpenChange, onSelect }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTag, setFilterTag] = useState('');

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMedia(filterTag);
      setMedia(res.data);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  }, [filterTag]);

  useEffect(() => {
    if (open) {
      loadMedia();
    }
  }, [open, loadMedia]);

  const handleSelect = (item) => {
    const fullUrl = resolveMediaUrl(item.url);
    onSelect(fullUrl);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Image className="w-5 h-5" /> Select from Media Library
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Filter by tag..."
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white"
          />
          {loading ? (
            <div className="text-center text-gray-400 py-8">Loading...</div>
          ) : media.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              No images in library. Upload images in the Media tab first.
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
              {media.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-sky-400 transition-all"
                >
                  <img
                    src={resolveMediaUrl(item.url)}
                    alt={item.alt_text || item.filename}
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Check className="w-8 h-8 text-sky-400" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 text-xs text-gray-300 truncate">
                    {item.filename}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { TeamNameAutocomplete, MediaPickerDialog };
